<template>
    <div class="page courses-page py-lg-6">
        <v-container :class="{'px-0': phoneOnly}">
            <div class="courses-toolbar mb-4 mb-lg-6" :class="{'px-4': phoneOnly}">
                <div
                    class="courses-filters-card"
                    :class="{
                        'courses-filters-card--dark': isDark,
                        'courses-filters-card--expanded': filtersExpanded || pcOnly,
                    }"
                >
                    <div
                        class="courses-filters-card__header"
                        :class="{'courses-filters-card__header--toggle': phoneOnly}"
                        :role="phoneOnly ? 'button' : undefined"
                        :tabindex="phoneOnly ? 0 : undefined"
                        @click="phoneOnly && toggleFilters()"
                        @keydown.enter.prevent="phoneOnly && toggleFilters()"
                        @keydown.space.prevent="phoneOnly && toggleFilters()"
                    >
                        <div class="courses-filters-card__icon-wrap">
                            <v-icon size="18" color="primary">mdi-tune-variant</v-icon>
                        </div>
                        <div class="flex-grow-1 min-width-0">
                            <div class="courses-filters-card__title strong-text--text font-weight-semibold">
                                بحث وفلترة
                            </div>
                            <div v-if="filtersExpanded || pcOnly" class="courses-filters-card__subtitle muted--text details-text">
                                ابحث عن دورة أو اختر من الفلاتر
                            </div>
                            <div v-else-if="activeFiltersLabel" class="courses-filters-card__active-summary details-text">
                                {{ activeFiltersLabel }}
                            </div>
                            <div v-else class="courses-filters-card__subtitle muted--text details-text">
                                اضغط لعرض البحث والفلاتر
                            </div>
                        </div>
                        <v-btn
                            v-if="hasActiveFilters"
                            small
                            depressed
                            rounded
                            color="primary"
                            class="courses-filters-card__clear flex-shrink-0"
                            @click.stop="resetFilters"
                        >
                            <v-icon left size="16">mdi-close-circle-outline</v-icon>
                            إلغاء
                        </v-btn>
                        <v-btn
                            v-if="phoneOnly"
                            icon
                            small
                            class="courses-filters-card__toggle flex-shrink-0"
                            :aria-expanded="filtersExpanded"
                            @click.stop="toggleFilters"
                        >
                            <v-icon :class="{'courses-filters-card__toggle-icon--open': filtersExpanded}">
                                mdi-chevron-down
                            </v-icon>
                        </v-btn>
                    </div>

                    <v-expand-transition>
                        <div v-show="filtersExpanded || pcOnly" class="courses-filters-card__body">
                            <form @submit.prevent="search" class="courses-filters-card__search mb-4">
                                <v-text-field
                                    v-model="searchQuery"
                                    ref="searchInput"
                                    placeholder="ابحث عن دورة..."
                                    prepend-inner-icon="mdi-magnify"
                                    hide-details
                                    clearable
                                    outlined
                                    dense
                                    rounded
                                    class="courses-search-field"
                                    @click:clear="onSearchClear"
                                />
                            </form>

                            <filter-chip-group
                                v-if="showSubjectFilter"
                                v-model="selectedSubject"
                                :items="subjectItems"
                                label="المادة"
                                icon="mdi-book-education-outline"
                                all-label="كل المواد"
                                class="mb-3"
                                @change="onFilterChange"
                            />

                            <div
                                v-if="showSubjectFilter && categoryItems.length"
                                class="courses-filters-card__divider"
                            ></div>

                            <filter-chip-group
                                v-if="categoryItems.length"
                                v-model="selectedCategory"
                                :items="categoryItems"
                                label="الفئة"
                                icon="mdi-tag-outline"
                                all-label="كل الفئات"
                                class="mb-3"
                                @change="onCategoryChange"
                            />

                            <div
                                v-if="showGradeFilter && categoryItems.length"
                                class="courses-filters-card__divider"
                            ></div>

                            <filter-chip-group
                                v-if="showGradeFilter"
                                v-model="selectedGrade"
                                :items="gradeItems"
                                label="الصف الدراسي"
                                icon="mdi-school-outline"
                                all-label="كل الصفوف"
                                @change="onGradeChange"
                            />
                        </div>
                    </v-expand-transition>
                </div>
            </div>

            <v-card class="pa-0 transparent" flat>
                <v-card-text class="py-lg-2" :class="{'pa-0': phoneOnly}">
                    <transition name="fade-transition" mode="out-in">
                        <template v-if="!loading || loadedOnce">
                            <infinity-wrapper v-if="courses && courses.length" @load="getCourses(false)" key="courses">
                                <v-row v-if="pcOnly" class="px-1">
                                    <v-col cols="12" md="6" lg="4" v-for="(course, i) in courses" :key="course.id + '-' + i" class="px-0 px-md-2 py-2">
                                        <course-card :course="course" />
                                    </v-col>
                                </v-row>
                                <v-list v-else two-line class="px-0">
                                    <course-list-item v-for="(course, i) in courses" :key="course.id + '-' + i" :course="course" />
                                </v-list>
                            </infinity-wrapper>
                            <div v-else class="pa-4 pa-lg-0">
                                <v-alert outlined color="blue-grey" text type="info" class="rounded-lg">
                                    <div class="medium-text">لم يتم العثور على دورات</div>
                                </v-alert>
                            </div>
                        </template>
                    </transition>
                </v-card-text>
            </v-card>
        </v-container>
    </div>
</template>

<script>
import GlobalComputed from '@/helpers/global-computed'
import GlobalMethods from '@/helpers/global-methods'
import InfinityWrapper from '@/components/default/infinity-wrapper'
import CourseCard from '@/components/custom/course-card'
import CourseListItem from '@/components/custom/course-list-item'
import FilterChipGroup from '@/components/custom/filter-chip-group'

export default {
    name: 'Courses',
    components: {
        InfinityWrapper,
        CourseCard,
        CourseListItem,
        FilterChipGroup,
    },
    data() {
        return {
            searchQuery: '',
            selectedCategory: null,
            selectedGrade: null,
            selectedSubject: null,
            loading: true,
            loadedOnce: false,
            filtersExpanded: false,
            syncingFilters: false,
        };
    },
    computed: {
        ...GlobalComputed,
        isDark() {
            return this.$vuetify.theme.dark;
        },
        courses() {
            return this.$store.state.coursesModule.courses;
        },
        categories() {
            return this.appData.categories || [];
        },
        grades() {
            return this.appData.grades || [];
        },
        subjects() {
            return this.appData.subjects || [];
        },
        showSubjectFilter() {
            return this.appData.subjects_enabled && this.subjects.length > 0;
        },
        showGradeFilter() {
            return !this.isAuthed && this.grades.length > 0;
        },
        categoryItems() {
            return this.categories.map(category => ({
                text: category.name,
                value: category.id,
                icon: 'mdi-tag-outline',
            }));
        },
        gradeItems() {
            return this.grades.map(grade => ({
                text: grade.name,
                value: grade.id,
                icon: 'mdi-school-outline',
            }));
        },
        subjectItems() {
            return this.subjects.map(subject => ({
                text: subject.name,
                value: subject.id,
                icon: subject.icon || 'mdi-book-education-outline',
            }));
        },
        hasActiveFilters() {
            return !!(this.searchQuery || this.selectedSubject || this.selectedCategory || this.selectedGrade);
        },
        activeFiltersLabel() {
            const parts = [];
            if (this.searchQuery) {
                parts.push(`"${this.searchQuery}"`);
            }
            if (this.selectedSubject) {
                const subject = this.subjectItems.find(item => item.value === this.selectedSubject);
                if (subject) {
                    parts.push(subject.text);
                }
            }
            if (this.selectedCategory) {
                const category = this.categoryItems.find(item => item.value === this.selectedCategory);
                if (category) {
                    parts.push(category.text);
                }
            }
            if (this.selectedGrade) {
                const grade = this.gradeItems.find(item => item.value === this.selectedGrade);
                if (grade) {
                    parts.push(grade.text);
                }
            }
            return parts.join(' · ');
        },
    },
    watch: {
        $route: {
            deep: true,
            handler($route) {
                if (this.syncingFilters) {
                    return;
                }
                this.syncFiltersFromRoute($route);
            },
        },
    },
    methods: {
        ...GlobalMethods,
        search() {
            this.getCourses(true);
            this.updateFiltersUrlQuery();
        },
        onSearchClear() {
            this.searchQuery = '';
            this.getCourses(true);
            this.updateFiltersUrlQuery();
        },
        onFilterChange() {
            this.getCourses(true);
            this.updateFiltersUrlQuery();
        },
        onCategoryChange() {
            if (this.selectedCategory) {
                this.selectedGrade = null;
            }
            this.onFilterChange();
        },
        onGradeChange() {
            if (this.selectedGrade) {
                this.selectedCategory = null;
            }
            this.onFilterChange();
        },
        resetFilters() {
            this.searchQuery = '';
            this.selectedSubject = null;
            this.selectedCategory = null;
            this.selectedGrade = null;
            this.getCourses(true);
            this.updateFiltersUrlQuery();
        },
        toggleFilters() {
            this.filtersExpanded = !this.filtersExpanded;
        },
        getCourses(initialLoad = false) {
            if (!this.loading || initialLoad) {
                this.loading = true;

                if (initialLoad) {
                    this.setLinearLoading();
                }

                const data = {};
                if (this.searchQuery) {
                    data.query = this.searchQuery;
                }
                if (this.selectedCategory) {
                    data.category_id = this.selectedCategory;
                } else if (this.selectedGrade) {
                    data.grade_id = this.selectedGrade;
                }
                if (this.selectedSubject) {
                    data.subject_id = this.selectedSubject;
                }

                this.$store.dispatch('getCourses', { initialLoad, data }).then(() => {
                    if (initialLoad) {
                        this.setLinearLoading(false);
                        this.loadedOnce = true;
                    }
                    this.loading = false;
                    if (this.$refs.searchInput) {
                        this.$refs.searchInput.blur();
                    }
                }).catch(() => {
                    if (initialLoad) {
                        this.setLinearLoading(false);
                    }
                    if (this.$route.name === 'courses') {
                        this.showMsg('حدثت مشكلة أثناء تحميل الدورات');
                    }
                    this.loading = false;
                });
            }
        },
        syncFiltersFromRoute($route = this.$route) {
            this.searchQuery = $route.query.search || '';
            this.selectedSubject = $route.query.subject_id ? parseInt($route.query.subject_id, 10) : null;
            this.selectedCategory = $route.query.category_id ? parseInt($route.query.category_id, 10) : null;
            this.selectedGrade = $route.query.grade_id ? parseInt($route.query.grade_id, 10) : null;
            this.getCourses(true);
        },
        updateFiltersUrlQuery() {
            this.syncingFilters = true;
            const query = {};
            if (this.searchQuery) {
                query.search = this.searchQuery;
            }
            if (this.selectedSubject) {
                query.subject_id = this.selectedSubject;
            }
            if (this.selectedCategory) {
                query.category_id = this.selectedCategory;
            }
            if (this.selectedGrade) {
                query.grade_id = this.selectedGrade;
            }

            this.$router.replace({ query }).then(() => {
                this.syncingFilters = false;
            }).catch(() => {
                this.syncingFilters = false;
            });
        },
    },
    mounted() {
        this.syncFiltersFromRoute();
        this.scrollToTop();
    },
};
</script>

<style lang="scss" scoped>
.courses-filters-card {
    background: #fff;
    border-radius: 16px;
    border: 1px solid rgba(0, 0, 0, 0.06);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.04);
    overflow: hidden;
}

.courses-filters-card--dark {
    background: var(--v-surface2-base, #1e1e1e);
    border-color: rgba(255, 255, 255, 0.08);
    box-shadow: none;
}

.courses-filters-card__header {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 16px 18px;
    background: #fff;
    border-bottom: 1px solid transparent;
    transition: border-color 0.2s ease;
}

.courses-filters-card--expanded .courses-filters-card__header {
    border-bottom-color: rgba(0, 0, 0, 0.06);
}

.courses-filters-card--dark .courses-filters-card__header {
    background: var(--v-surface2-base, #1e1e1e);
}

.courses-filters-card--dark.courses-filters-card--expanded .courses-filters-card__header {
    border-bottom-color: rgba(255, 255, 255, 0.08);
}

.courses-filters-card__header--toggle {
    cursor: pointer;
    user-select: none;

    &:hover {
        background: rgba(0, 0, 0, 0.015);
    }
}

.courses-filters-card--dark .courses-filters-card__header--toggle:hover {
    background: rgba(255, 255, 255, 0.03);
}

.courses-filters-card__icon-wrap {
    width: 38px;
    height: 38px;
    border-radius: 12px;
    background: rgba(0, 35, 91, 0.07);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
}

.courses-filters-card--dark .courses-filters-card__icon-wrap {
    background: rgba(84, 158, 255, 0.12);
}

.courses-filters-card__title {
    font-size: 0.95rem;
    line-height: 1.3;
}

.courses-filters-card__subtitle {
    margin-top: 2px;
    line-height: 1.4;
}

.courses-filters-card__active-summary {
    margin-top: 4px;
    color: var(--v-primary-base);
    font-weight: 600;
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.courses-filters-card__clear {
    text-transform: none;
    font-weight: 600;
    letter-spacing: 0;
}

.courses-filters-card__toggle-icon--open {
    transform: rotate(180deg);
}

.courses-filters-card__toggle .v-icon {
    transition: transform 0.25s ease;
}

.courses-filters-card__body {
    padding: 16px 18px 18px;
    background: #fff;
}

.courses-filters-card--dark .courses-filters-card__body {
    background: var(--v-surface2-base, #1e1e1e);
}

.courses-filters-card__divider {
    height: 1px;
    margin: 14px 0;
    background: rgba(0, 0, 0, 0.06);
}

.courses-filters-card--dark .courses-filters-card__divider {
    background: rgba(255, 255, 255, 0.08);
}

.courses-filters-card ::v-deep .courses-search-field .v-input__slot {
    background: #f4f6f9 !important;
    border-color: rgba(0, 0, 0, 0.08) !important;
    min-height: 44px !important;
}

.courses-filters-card--dark ::v-deep .courses-search-field .v-input__slot {
    background: rgba(255, 255, 255, 0.06) !important;
    border-color: rgba(255, 255, 255, 0.1) !important;
}

.courses-filters-card ::v-deep .courses-search-field.v-input--is-focused .v-input__slot {
    border-color: var(--v-primary-base) !important;
    box-shadow: 0 0 0 3px rgba(0, 35, 91, 0.08);
}

@media (max-width: 959px) {
    .courses-filters-card {
        border-radius: 14px;
    }

    .courses-filters-card__header,
    .courses-filters-card__body {
        padding-left: 16px;
        padding-right: 16px;
    }
}
</style>

<style lang="scss">
.v-skeleton-loader__image {
    height: 150px !important;
}
</style>
